import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import classNames from 'classnames';
import get from 'lodash/get';
import { store } from 'data';
import './mention.css';
import { MentionsDataParam } from './constants';
import { MentionItem } from './types';

interface CkEditorMentionAttribute extends MentionItem {
  uid: string;
}

let userId = 0;

function listener() {
  const storeData = store.getState();

  // The correct user id will be set, before we do the downcasting.(* most of the time)
  // The cost of re-rendering the full editor is heavier than this edge case.
  userId = get(storeData, ['currentUserProfile', 'data', 'user', 'userId']);
}

store.subscribe(listener);

/**
 * BE expects projectId to be set in template mentions only if it exists
 * For new templates, it can be left empty.
 * TODO: to fix bug where clicking on document template in description
 * is not redirecting to the correct url.
 */
const isValidProjectId = (projectId) => projectId && projectId !== 'new';

export class MentionCustomization extends Plugin {
  static get pluginName(): string {
    return 'MentionCustomization';
  }

  init(): void {
    const { editor } = this;

    // The upcast converter will convert <a class="mention" href="" data-user-id="">
    // elements to the model 'mention' attribute.
    editor.conversion.for('upcast').elementToAttribute({
      view: {
        name: 'a',
        key: 'data-mention',
        attributes: {
          href: true,
          [MentionsDataParam.ObjectId]: true,
          [MentionsDataParam.Type]: true,
          'data-mention': true,
        },
      },
      model: {
        key: 'mention',
        value: (viewItem) => {
          const projectId = viewItem.getAttribute(MentionsDataParam.ProjectId);
          const mentionAttribute = editor.plugins
            .get<any>('Mention')
            .toMentionAttribute(viewItem, {
              name: viewItem.getAttribute('data-mention'),
              content: viewItem.getAttribute('data-mention'),
              link: viewItem.getAttribute('href'),
              type: viewItem.getAttribute(MentionsDataParam.Type),
              subType: viewItem.getAttribute(MentionsDataParam.SubType),
              objectId: viewItem.getAttribute(MentionsDataParam.ObjectId),
              ...(isValidProjectId(projectId) && {
                projectId,
              }),
              spaceId: viewItem.getAttribute(MentionsDataParam.SpaceId),
              identifier: viewItem.getAttribute(MentionsDataParam.Identifier),
            });

          return mentionAttribute;
        },
      },
      converterPriority: 'high',
    });

    editor.conversion.for('downcast').attributeToElement({
      model: 'mention',
      view: (modelAttributeValue: CkEditorMentionAttribute, { writer }) => {
        // Do not convert empty attributes (lack of value means no mention).
        if (!modelAttributeValue) {
          return;
        }

        const classList = classNames('rl__mention', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          rl__mention__self: userId === Number(modelAttributeValue.objectId),
        });

        const {
          type,
          objectId,
          identifier,
          name,
          spaceId,
          subType,
          projectId,
        } = modelAttributeValue;

        const attributes = {
          class: classList,
          [MentionsDataParam.Type]: type,
          ...(isValidProjectId(projectId) && {
            [MentionsDataParam.ProjectId]: projectId,
          }),
          [MentionsDataParam.ObjectId]: objectId,
          [MentionsDataParam.Identifier]: identifier,
          'data-mention': name,
          href: '#',
        };

        if (spaceId) {
          attributes[MentionsDataParam.SpaceId] = spaceId;
        }

        if (subType) {
          attributes[MentionsDataParam.SubType] = subType;
        }

        // eslint-disable-next-line consistent-return
        return writer.createAttributeElement('a', attributes, {
          priority: 20,
          id: modelAttributeValue.uid,
        });
      },
      converterPriority: 'high',
    });
  }
}
